import type { AuditChange, AuditRow } from "@app/portal-sdk";
import { Typography } from "@app/ui-kit";

/**
 * CORE-1: what an audit entry changed, as a sentence: "changed Revenue basis
 * from Wholesale to Retail". The label is the client's own word for a code,
 * resolved by the API; a field recorded without its values says so.
 */
const shown = (value: unknown, label: string | null): string => {
  if (label) return label;
  if (value === null || value === undefined || value === "") return "nothing";
  if (typeof value === "boolean") return value ? "yes" : "no";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const phrase = (c: AuditChange, kind: AuditRow["kind"]): string => {
  if (c.redacted) return `changed ${c.label} (values not recorded)`;
  if (kind === "create") return `set ${c.label} to ${shown(c.to, c.toLabel)}`;
  if (kind === "delete") return `${c.label} was ${shown(c.from, c.fromLabel)}`;
  return `changed ${c.label} from ${shown(c.from, c.fromLabel)} to ${shown(c.to, c.toLabel)}`;
};

export const auditSentence = (row: Pick<AuditRow, "changes" | "kind">): string | null => {
  if (!row.changes || row.changes.length === 0) return null;
  const parts = row.changes.map((c) => phrase(c, row.kind));
  if (row.kind === "delete") return `removed; ${parts.join(", ")}`;
  return parts.join("; ");
};

export const AuditChanges = ({ row }: { row: Pick<AuditRow, "changes" | "kind"> }) => {
  const sentence = auditSentence(row);
  if (!sentence) return null;
  return <Typography variant="body2" sx={{ color: "text.secondary" }}>{sentence}</Typography>;
};
